import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  AlertCircle, Loader2, Stethoscope, Pill, ShoppingCart, Boxes, ClipboardList,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Single-URL login. The user_id prefix decides which auth path to take:
//   pharmacystores* → staff-login role=pharmacystores → /pharmacy-stores
//   storesadmin*    → staff-login role=storesadmin    → /stores-admin
//   pharmacy*       → staff-login role=pharmacy       → /pharmacy
//   purchase*       → staff-login role=purchase       → /purchase
//   anything else   → doctor-login                    → /dashboard
//
// IMPORTANT prefix order: `pharmacystores` MUST be checked BEFORE `pharmacy`
// (longest prefix wins) — `pharmacystores01`.startsWith('pharmacy') is also
// true, so a naive check would route a stores keeper to the Pharmacy portal.
function detectRole(userId) {
  const id = (userId || '').trim().toLowerCase();
  if (!id) return null;
  if (id.startsWith('pharmacystores')) return 'pharmacystores';
  if (id.startsWith('storesadmin')) return 'storesadmin';
  if (id.startsWith('pharmacy')) return 'pharmacy';
  if (id.startsWith('purchase')) return 'purchase';
  return 'doctor';
}

// Visual chrome per detected role — keeps the login screen feeling tailored
// to the user while staying on a single URL.
const ROLE_CHROME = {
  doctor: {
    title: 'Doctor Login', subtitle: 'Smart clinical consultation workflow',
    icon: Stethoscope, idLabel: 'Doctor ID', idPlaceholder: 'Enter your Doctor ID',
    accent: 'from-blue-800 via-blue-900 to-teal-900', btn: 'bg-blue-700 hover:bg-blue-800',
    badge: null,
  },
  pharmacy: {
    title: 'Pharmacy Login', subtitle: 'Dispensing & prescription fulfilment',
    icon: Pill, idLabel: 'Pharmacy User ID', idPlaceholder: 'e.g. pharmacy01',
    accent: 'from-emerald-700 via-emerald-800 to-teal-900', btn: 'bg-emerald-700 hover:bg-emerald-800',
    badge: { label: 'Pharmacy', cls: 'bg-emerald-500/20 text-emerald-100 border-emerald-300/30' },
  },
  purchase: {
    title: 'Purchase Login', subtitle: 'Vendor management & procurement',
    icon: ShoppingCart, idLabel: 'Purchase User ID', idPlaceholder: 'e.g. purchase01',
    accent: 'from-blue-800 via-blue-900 to-indigo-900', btn: 'bg-blue-700 hover:bg-blue-800',
    badge: { label: 'Purchase', cls: 'bg-blue-500/20 text-blue-100 border-blue-300/30' },
  },
  pharmacystores: {
    title: 'Pharmacy Stores Login', subtitle: 'Stockroom indents & requisitions',
    icon: Boxes, idLabel: 'Pharmacy Stores User ID', idPlaceholder: 'e.g. pharmacystores01',
    accent: 'from-amber-700 via-orange-800 to-amber-900', btn: 'bg-amber-700 hover:bg-amber-800',
    badge: { label: 'Pharmacy Stores', cls: 'bg-amber-500/20 text-amber-100 border-amber-300/30' },
  },
  storesadmin: {
    title: 'Stores Admin Login', subtitle: 'PR review & PO generation',
    icon: ClipboardList, idLabel: 'Stores Admin User ID', idPlaceholder: 'e.g. storesadmin01',
    accent: 'from-purple-800 via-violet-900 to-fuchsia-900', btn: 'bg-purple-700 hover:bg-purple-800',
    badge: { label: 'Stores Admin', cls: 'bg-purple-500/20 text-purple-100 border-purple-300/30' },
  },
};

function homeForRole(role) {
  if (role === 'pharmacy') return '/pharmacy';
  if (role === 'purchase') return '/purchase';
  if (role === 'pharmacystores') return '/pharmacy-stores';
  if (role === 'storesadmin') return '/stores-admin';
  return '/dashboard';
}

export default function LoginPage() {
  const { setUser } = useAuth();
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Live-detect role as the user types so the chrome (color, label, icon)
  // reflects the destination portal. Stays on `doctor` until they start
  // typing a recognised prefix.
  const detectedRole = useMemo(() => detectRole(userId) || 'doctor', [userId]);
  const chrome = ROLE_CHROME[detectedRole] || ROLE_CHROME.doctor;
  const Icon = chrome.icon;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const role = detectRole(userId);
    try {
      let data;
      if (role === 'doctor') {
        // Doctor login keeps its password requirement (any pw works per
        // dummy-auth posture, but we still need to send something).
        const res = await axios.post(`${API_URL}/api/auth/doctor-login`, {
          doctor_id: userId.trim(),
          password: password || 'any',
        }, { withCredentials: true });
        data = res.data;
      } else {
        const res = await axios.post(`${API_URL}/api/auth/staff-login`, {
          user_id: userId.trim(),
          role,
          password: password,
        }, { withCredentials: true });
        data = res.data;
      }
      setUser(data);
      // Hard-navigate so AuthContext re-hydrates from /api/auth/me with
      // the freshly-set cookie. Falls back to the role-derived home if the
      // backend didn't echo a role.
      window.location.assign(homeForRole(data.role));
    } catch (err) {
      let msg = 'Login failed. Please check your User ID.';
      if (!err.response) {
        msg = 'Cannot reach server. Check your network or try again shortly.';
      } else if (err.response.status === 404) {
        msg = 'Login service unavailable. The server may be updating — please retry in a minute.';
      } else if (err.response.data?.detail) {
        const d = err.response.data.detail;
        msg = typeof d === 'string' ? d : msg;
      }
      setError(msg);
    }
    setLoading(false);
  };

  // Doctor login is the only one that requires a password input (the staff
  // logins use dummy auth — but we keep the field visible so the form
  // shape stays consistent and doesn't jolt when the prefix is recognised).
  // Pharmacy & StoresAdmin are PROTECTED — they validate the password.
  const isStaff = detectedRole !== 'doctor';
  const PROTECTED_ROLES = ['pharmacy', 'storesadmin'];
  const isPasswordRequired = !isStaff || PROTECTED_ROLES.includes(detectedRole);
  const passwordPlaceholder =
    detectedRole === 'pharmacy' ? 'Enter pharmacy password'
    : detectedRole === 'storesadmin' ? 'Enter stores admin password'
    : isStaff ? 'staff123 (any)'
    : 'Password';

  return (
    <div
      className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${chrome.accent} px-4 transition-colors duration-500`}
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="h-16 w-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
            <Icon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
            ECare 360
          </h1>
          <p className="text-white/70 mt-2 text-base">{chrome.subtitle}</p>
        </div>

        <Card className="shadow-2xl border-0">
          <CardHeader className="pb-4">
            <CardTitle
              className="text-xl text-center flex items-center justify-center gap-2"
              style={{ fontFamily: 'Manrope' }}
              data-testid="login-detected-role-title"
            >
              {chrome.title}
              {chrome.badge && (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${chrome.badge.cls.replace('text-', 'text-').replace(/text-[a-z]+-100/, (m) => m.replace('100', '700')).replace(/bg-[a-z]+-500\/20/, (m) => m.replace('500/20', '100')).replace(/border-[a-z]+-300\/30/, (m) => m.replace('300/30', '200'))}`}>
                  Auto-detected
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div
                  className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                  data-testid="login-error"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />{error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="doctor-id" className="text-sm font-medium">{chrome.idLabel}</Label>
                <Input
                  id="doctor-id"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder={chrome.idPlaceholder}
                  className="h-12 text-base"
                  required
                  autoFocus
                  data-testid="login-doctor-id"
                />
                {isStaff && (
                  <p className="text-[11px] text-slate-500" data-testid="login-staff-hint">
                    Detected as <b>{chrome.title.replace(' Login', '')}</b>. Submit to enter.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={passwordPlaceholder}
                  className="h-12 text-base"
                  required={isPasswordRequired}
                  data-testid="login-password"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !userId.trim()}
                className={`w-full h-12 text-base ${chrome.btn}`}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />Signing In...
                  </>
                ) : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
